import { useCallback, useEffect, useLayoutEffect, useState } from 'react'
import type { QueryClient } from '@tanstack/react-query'
import { fetchAndCacheList, fileKeys, reaskOnEmailChange, type ListQueryOptions } from '@papan/files/client/queries'

/**
 * #1058 — a `resync` (events missed while the socket was down) means any folder
 * this tree has loaded may be out of date, not only the ones an event would
 * name. Mark every loaded folder stale, keeping its children mounted (no
 * collapse, no flicker): an EXPANDED row reloads itself through the tree's
 * auto-load (`expanded && !isLoaded`), a collapsed one when next opened.
 *
 * The workspace explorer keeps its own tree state (not a TanStack cache), so
 * `FileListCacheSync` can't reach it; it calls this from its own `onResync`
 * while mounted. Its root comes from a query, and a refetch that returns the
 * same rows keeps the same `data` object (structural sharing), so the root's
 * merge — which would mark the rows below stale — never runs: without this an
 * expanded folder that changed during the disconnect would keep its old rows.
 * (A linked-folder tree loads its root imperatively, so its merge always runs.)
 *
 * A folder whose last load FAILED (`loadFailed`, which stops the row's
 * automatic loads from re-asking it in a loop) is retried too: the resync is
 * a new chance.
 */
export function markTreeStale<T extends { isFolder: boolean; isLoaded?: boolean; isLoading?: boolean; loadFailed?: boolean; children?: T[] }>(nodes: T[]): T[] {
  return nodes.map(node => {
    if (!node.isFolder) return node
    const children = node.children?.length ? markTreeStale(node.children) : node.children
    // `isLoading: false`: a load still in flight was asked before the
    // reconnect and settles nothing (`LatestFolderLoads`) — it asks again.
    return { ...node, isLoaded: false, isLoading: false, loadFailed: false, children }
  })
}

/**
 * The ids of the folders this tree has LOADED — fetched now (`isLoaded`), or
 * earlier and kept stale by a parent refresh or a resync (rows still there) —
 * for a permission change to re-read, plus any whose last load FAILED
 * (`loadFailed`: someone tried to open it; the permission change is a new
 * chance, as a resync is). NOT a folder whose first load is still in flight: a
 * re-ask would join that same fetch (`fetchQuery` shares a running fetch), so it
 * would read nothing newer — the accepted I4 residual (see I262). Every folder row starts as `children: []`, so
 * testing `children` alone picks every folder ever LISTED: one request each, a
 * level deeper on every event — a few team-member adds flooded the read rate
 * limit (gnexis 2026-10-21).
 */
export function loadedFolderIds<T extends { id: string; isLoaded?: boolean; loadFailed?: boolean; children?: T[] }>(nodes: T[]): string[] {
  const ids: string[] = []
  const walk = (items: T[]) => {
    for (const n of items) {
      if (!n.isLoaded && !n.loadFailed && !n.children?.length) continue
      ids.push(n.id)
      walk(n.children ?? [])
    }
  }
  walk(nodes)
  return ids
}


/** One load of a folder: fetch (past the cache when `fresh`), then `settle(write, ok)`. */
export type FolderLoad = (
  folderId: string,
  settle: (write: () => void, ok?: boolean) => Promise<void>,
  opts: { fresh: boolean; reask: boolean },
) => Promise<void>

/**
 * The tree's folder loads, coordinated (#1058 code review). A folder may have
 * several loads in flight (a click, a realtime refresh, a resync's re-ask), and
 * they can answer in any order:
 *  - only the LATEST load of a folder settles it (writes its rows, its
 *    `isLoading`, its failure); an older call resolves once the latest has
 *    settled — whatever its outcome — so `await` on any of them means "the
 *    folder has settled", not "the load succeeded";
 *  - a load asked before the latest RESYNC settles nothing — its answer may
 *    predate what the socket missed — and asks again, expanded folder or
 *    collapsed, success or failure, through the latest COMMITTED loader
 *    (`setLoader`) — the overtaken load's closure captured the tree as it was
 *    then — and not at all once the tree has unmounted (`detach`);
 *  - a load is `fresh` — the caller must read past its cache (`staleTime: 0`)
 *    — when it is such a re-ask, or a folder's first load within
 *    `FRESH_WINDOW_MS` of the latest resync. Marking the cache stale is not
 *    enough: a pre-reconnect read finishing after the mark stamps its key fresh
 *    again. (A read still IN FLIGHT is shared by `fetchQuery`'s de-dupe
 *    whatever the staleTime; since #1210 the resync is an access-change
 *    boundary, so that read asks again inside its own fetch and seeds nothing
 *    from its pre-reconnect answer — `markResyncBoundary`.) Each tree re-asks on its own, so trees
 *    showing the same folder may each fetch it once after a reconnect —
 *    accepted for a rare event.
 *
 * Components use it through `useLatestFolderLoads`.
 */
export class LatestFolderLoads {
  /**
   * How long after a resync a folder's first NEW load still reads past the
   * cache: long enough for a read in flight at the reconnect to have landed
   * (and stamped its key fresh) in the normal case, short enough that one
   * reconnect doesn't turn off the 1 s cache sharing between sibling trees for
   * the rest of the session. A read slower than this can't answer a later open
   * with its pre-reconnect rows: it started before the resync boundary, so it
   * reads again inside its fetch (#1210). A RE-ASK always reads past the cache.
   */
  static readonly FRESH_WINDOW_MS = 30_000

  private loads = new Map<string, { token: object; done: Promise<void> }>()
  private epoch = 0
  private resyncAt: number | null = null
  /** Folders whose load succeeded AND whose write ran, since the latest resync, inside its window. */
  private settledSinceResync = new Set<string>()
  /**
   * The loader re-asks run through: the tree's last COMMITTED one (unset only
   * in direct use, as in tests — a re-ask then reuses the overtaken load).
   */
  private loader: FolderLoad | undefined = undefined
  /**
   * Whether the tree is mounted. Set by `attach`/`detach` from a passive effect
   * keyed on this instance only, and the SETUP sets it back: StrictMode's dev
   * remount runs detach then attach on the same instance (CLAUDE.md warns
   * that a flag only a cleanup writes breaks there). Today no code path hides
   * a tree by running its effect cleanup (keep-alive decks use
   * `display: none`); a tree kept alive with React's `<Activity
   * mode="hidden">` would drop the re-asks due while hidden — revisit then.
   * In the short gap between an unmount's commit and its passive cleanup,
   * each load in flight that a resync overtook still re-asks once: a fresh read
   * (or one already in flight for that key) that writes the shared list and
   * permission caches, with its caller waiting for it; the tree's state writes
   * are ignored — accepted. Starts true for direct use.
   */
  private attached = true

  /** `now` is monotonic by default — a wall-clock change must not move the window. */
  constructor(private readonly now: () => number = () => performance.now()) {}

  /** The component's latest committed loader (set from a layout effect). */
  setLoader(load: FolderLoad): void {
    this.loader = load
  }

  attach(): void {
    this.attached = true
  }

  /**
   * The tree unmounted: a re-ask due later asks nothing (no fetch, no cache
   * write, no wait). The loader is left as it is — the layout effect owns
   * it. While any load is still in flight it keeps this instance, and so the
   * last committed loader (its render scope), reachable; a fetch that never
   * settles keeps it indefinitely, as it keeps its own closure anyway.
   */
  detach(): void {
    this.attached = false
  }

  /** A resync: every load in flight was asked before it. */
  resync(): void {
    this.epoch++
    this.resyncAt = this.now()
    this.settledSinceResync.clear()
  }

  /** @internal Loads registered and not yet settled — for tests only. */
  get pending(): number {
    return this.loads.size
  }

  private withinWindow(): boolean {
    return this.resyncAt !== null && this.now() - this.resyncAt < LatestFolderLoads.FRESH_WINDOW_MS
  }

  /**
   * Run one load of `folderId` with `load` (a component's first loads use its
   * render's loader; re-asks use `setLoader`'s, falling back to `load` only
   * when none was set — direct use, as in tests). The returned promise is this
   * load's own outcome; a SUPERSEDED load resolves once the latest has
   * settled, whatever that one's outcome.
   */
  run(folderId: string, load: FolderLoad, reask = false): Promise<void> {
    const token = {}
    const epoch = this.epoch
    const fresh = reask || (this.withinWindow() && !this.settledSinceResync.has(folderId))
    // Registered BEFORE `load` runs: a load may settle synchronously (one that
    // decides to ask nothing), and must find itself the latest.
    let finish!: (p: Promise<void>) => void
    const done = new Promise<void>((resolve) => { finish = resolve as (p: Promise<void>) => void })
    this.loads.set(folderId, { token, done })
    // `settle` never rejects: a superseded load waits for the latest quietly, a
    // re-ask's own failure is the re-ask's (its own load handles it).
    const settle = async (write: () => void, ok = true): Promise<void> => {
      const newest = this.loads.get(folderId)
      if (newest?.token !== token) return newest?.done.then(() => {}, () => {}) // superseded: wait for the latest
      this.loads.delete(folderId)
      if (epoch !== this.epoch) {
        // Asked before a resync: ask again, through the latest committed
        // loader — unless the tree is gone. Its own failure is logged, not
        // passed on.
        if (!this.attached) return
        return this.run(folderId, this.loader ?? load, true).then(
          () => {},
          (err) => { console.error('[tree] re-ask after a resync failed:', folderId, err) },
        )
      }
      try {
        write()
      } catch (err) {
        console.error('[tree] writing a folder load failed:', folderId, err)
        return
      }
      // Only a SUCCESS uses up the folder's fresh read: a retry after a failure
      // must not be answered by a pre-reconnect cache entry. ("Written" means the
      // write ran without throwing; a state UPDATER it queues runs at render.)
      if (ok && this.withinWindow()) this.settledSinceResync.add(folderId)
    }
    // Catch a synchronous throw too, and drop the entry however the load ends
    // (one that never settles included).
    let outcome: Promise<void>
    try {
      outcome = Promise.resolve(load(folderId, settle, { fresh, reask }))
    } catch (err) {
      outcome = Promise.reject(err)
    }
    outcome = outcome.finally(() => {
      if (this.loads.get(folderId)?.token === token) this.loads.delete(folderId)
    })
    finish(outcome)
    return done
  }
}

/**
 * A tree's coordinated folder loader.
 *  - `load(folderId)` runs `loader` through `LatestFolderLoads`.
 *  - A re-ask after a resync runs through the loader of the latest COMMITTED
 *    render (set in a layout effect, never during render, so a render React
 *    discards never becomes it), and not at all once the tree's unmount
 *    cleanup ran (`attached` describes the short gap before it).
 *  - `resync()` is what the tree's `onResync` calls.
 * (The loader still reads tree refs the component writes during render; under
 * concurrent rendering a discarded render could be seen for a moment —
 * accepted.)
 */
export function useLatestFolderLoads(loader: FolderLoad): { load: (folderId: string) => Promise<void>; resync: () => void } {
  const [loads] = useState(() => new LatestFolderLoads())
  useLayoutEffect(() => { loads.setLoader(loader) }, [loads, loader])
  useEffect(() => {
    loads.attach()
    return () => loads.detach()
  }, [loads])
  const load = useCallback((folderId: string) => loads.run(folderId, loader), [loads, loader])
  const resync = useCallback(() => loads.resync(), [loads])
  return { load, resync }
}

/**
 * Mark every cached folder list stale without refetching — what each tree does
 * first on a resync, so a reload (`fetchQuery`, 1 s `staleTime`) skips a cached
 * answer from before it. Only until the key's next successful fetch: a
 * pre-reconnect read finishing afterwards stamps it fresh again, which is why
 * `LatestFolderLoads` also marks a folder's first load after a resync `fresh`.
 * Never a cancel: the same keys are read by the drive, pickers and the
 * pending-chain check (I4).
 */
export function markListCacheStale(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: [...fileKeys.all, 'list'], refetchType: 'none' })
}

/**
 * #1210 — a tree's folder read: `fetchAndCacheList`, asked once more when the org email changed during it
 * (`IdentityChangedError` — its answer was for the identity that is no longer active; the new ask builds its key for
 * the current one). The trees have no identity dependency of their own, so nothing else would ask again. A TENANT
 * change (the folder belongs to the other tenant) or a second refusal is thrown; the loaders settle it quietly, never
 * as a failed load.
 */
export async function readTreeFolder(
  queryClient: QueryClient,
  folderId: string,
  opts: ListQueryOptions,
  staleTime: number,
): ReturnType<typeof fetchAndCacheList> {
  let asked = 0
  return reaskOnEmailChange(() => fetchAndCacheList(queryClient, folderId, opts, { staleTime: asked++ ? 0 : staleTime }))
}
